import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { customCollectionsAPI, CustomCollection } from "../services/api";

const ProgressDashboard: React.FC = () => {
  const {
    getAccessTokenSilently,
    isAuthenticated,
    isLoading: authLoading,
  } = useAuth0();
  const [collections, setCollections] = useState<CustomCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    // Only fetch progress if user is authenticated and auth is not loading
    if (!isAuthenticated || authLoading) {
      if (!authLoading) {
        setLoading(false);
      }
      return;
    }

    const fetchProgress = async () => {
      try {
        const response = await customCollectionsAPI.getCollections(
          getAccessTokenSilently
        );
        setCollections(response.data.collections || []);
      } catch (err: any) {
        console.error("Progress dashboard error:", err);
        setError("Failed to load progress data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchProgress();
  }, [getAccessTokenSilently, isAuthenticated, authLoading]);

  const ProgressBar: React.FC<{
    completed: number;
    total: number;
    label: string;
    subtitle?: string;
    color?: string;
  }> = ({ completed, total, label, subtitle, color = "blue" }) => {
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

    // Map color names to actual Tailwind classes
    const colorClasses: { [key: string]: string } = {
      cyan: "bg-cyan-600 dark:bg-cyan-500",
      blue: "bg-blue-600 dark:bg-blue-500",
      teal: "bg-teal-600 dark:bg-teal-500",
      indigo: "bg-indigo-600 dark:bg-indigo-500",
      purple: "bg-purple-600 dark:bg-purple-500",
    };

    const barColorClass = colorClasses[color] || colorClasses.blue;

    return (
      <div className="mb-4">
        <div className="flex justify-between items-center mb-1">
          <div>
            <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
              {label}
            </span>
            {subtitle && (
              <span className="text-xs text-zinc-500 dark:text-zinc-400 ml-2">
                {subtitle}
              </span>
            )}
          </div>
          <span className="text-sm text-zinc-600 dark:text-zinc-400">
            {completed} / {total} ({percentage}%)
          </span>
        </div>
        <div className="w-full bg-zinc-200 dark:bg-zinc-700 rounded-full h-3">
          <div
            className={`${barColorClass} h-3 rounded-full transition-all duration-300`}
            style={{ width: `${percentage}%` }}
          ></div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
        <button
          onClick={() => window.location.reload()}
          className="bg-cyan-600 text-white px-4 py-2 rounded hover:bg-cyan-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Filter collections to only show those with verses and progress
  const filteredCollections = collections.filter((collection) => {
    // Only show collections with verses
    if (!collection.verse_count || collection.verse_count === 0) return false;

    // Show if it has either clozes or verbs
    return (
      (collection.total_clozes && collection.total_clozes > 0) ||
      (collection.total_verbs && collection.total_verbs > 0)
    );
  });

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-zinc-900 dark:text-zinc-100 mb-2">
          Learning Progress
        </h1>
        <p className="text-zinc-600 dark:text-zinc-400">
          Track your completion and mastery progress across all decks.
        </p>
        <p className="text-sm text-zinc-500 dark:text-zinc-500 mt-2">
          <strong>Completion:</strong> Answered correctly at least once •{" "}
          <strong>Mastery:</strong> Answered correctly 3+ times
        </p>
      </div>

      {filteredCollections.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-zinc-500 dark:text-zinc-400 mb-4">
            <svg
              className="mx-auto h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-2">
            No progress data available
          </h3>
          <p className="text-zinc-500 dark:text-zinc-400 mb-4">
            Start practicing to see your progress here!
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredCollections.map((collection) => {
            const hasClozes =
              collection.total_clozes && collection.total_clozes > 0;
            const hasVerbs =
              collection.total_verbs && collection.total_verbs > 0;

            return (
              <div
                key={collection.id}
                className="bg-white dark:bg-zinc-800 rounded-lg shadow-md border border-zinc-200 dark:border-zinc-700 p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100 mb-1">
                      {collection.name}
                    </h3>
                    <div className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
                      {collection.verse_count} verses
                    </div>
                  </div>
                </div>

                {/* Cloze Progress - Always show */}
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-zinc-700 dark:text-zinc-300 mb-2">
                    Cloze Exercises
                  </h4>
                  <ProgressBar
                    completed={collection.completed_clozes || 0}
                    total={collection.total_clozes || 0}
                    label="Completion"
                    subtitle="correct at least once"
                    color="teal"
                  />
                  <ProgressBar
                    completed={collection.mastered_clozes || 0}
                    total={collection.total_clozes || 0}
                    label="Mastery"
                    subtitle="correct 3+ times"
                    color="indigo"
                  />
                </div>

                {/* Verb Parsing Progress - Always show */}
                <div>
                  <h4 className="text-sm font-semibold text-zinc-700 dark:text-zinc-300 mb-2">
                    Verb Parsing
                  </h4>
                  <ProgressBar
                    completed={collection.completed_verbs || 0}
                    total={collection.total_verbs || 0}
                    label="Completion"
                    subtitle="correct at least once"
                    color="cyan"
                  />
                  <ProgressBar
                    completed={collection.mastered_verbs || 0}
                    total={collection.total_verbs || 0}
                    label="Mastery"
                    subtitle="correct 3+ times"
                    color="purple"
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProgressDashboard;
